import { useEffect } from 'react';
import Script from 'next/script';
import config from '../../../util/load-config';
import {
  autoSubscribeToPushNotifications,
  BLOOMREACH_STARTED_EVENT,
} from '../../../util/bloomreach';
import { hasGivenCookieConsent } from '../../../util/cookie-consent';
import { useHasMounted } from 'components/_shared/widgets/render-control';

interface BloomreachInitConfig {
  target: string;
  stream_id: string;
  experimental: {
    non_personalized_weblayers: boolean;
  };
  track: {
    visits: boolean;
  };
  service_worker_path: string;
}

const isValidBloomreachInitConfig = (
  value: unknown
): value is BloomreachInitConfig => {
  if (typeof value !== 'object' || value === null) return false;

  const {
    target,
    stream_id,
    experimental,
    track,
    service_worker_path,
  } = value as Record<string, unknown>;

  return (
    typeof target === 'string' &&
    target.length > 0 &&
    typeof stream_id === 'string' &&
    stream_id.length > 0 &&
    typeof experimental === 'object' &&
    experimental !== null &&
    typeof (experimental as Record<string, unknown>)
      .non_personalized_weblayers === 'boolean' &&
    typeof track === 'object' &&
    track !== null &&
    typeof (track as Record<string, unknown>).visits === 'boolean' &&
    typeof service_worker_path === 'string' &&
    service_worker_path.length > 0
  );
};

// builds + validates the SDK init config; identify() happens separately, see util/bloomreach.ts
const buildBloomreachConfig = (): string | null => {
  const bloomreachConfig = {
    target: config.bloomreach.target,
    stream_id: config.bloomreach.streamId,
    experimental: {
      non_personalized_weblayers: true,
    },
    // Datacop specifically asked us not to track page visits
    track: {
      visits: false,
    },
    service_worker_path: '/bloomreach-sw.js',
  };

  if (!isValidBloomreachInitConfig(bloomreachConfig)) {
    console.error(
      'Bloomreach - built config failed validation',
      bloomreachConfig
    );
    return null;
  }

  return JSON.stringify(bloomreachConfig);
};

// Bloomreach's own snippet (Settings > Web Integration), verbatim - don't hand-edit the minified blob.
// when regenerating it from the dashboard, keep the appended lines after it (see the comment there)
// @see https://documentation.bloomreach.com/engagement/docs/javascript-sdk-integration
const BloomreachInit = () => {
  const hasMounted = useHasMounted();
  // cookie is only readable client-side, so wait for mount to avoid an SSR/hydration mismatch
  const shouldLoadSdk =
    hasMounted && !!config.bloomreach?.streamId && hasGivenCookieConsent();

  // migrates users who already granted browser notification permission (e.g. via Frizbit).
  // gated on shouldLoadSdk so it only fires on the commit that mounts the SDK snippet below,
  // never on the first (null) render - the util then waits for the SDK before subscribing
  useEffect(() => {
    if (!shouldLoadSdk) return;
    autoSubscribeToPushNotifications();
  }, [shouldLoadSdk]);

  if (!shouldLoadSdk) return null;

  const bloomreachConfig = buildBloomreachConfig();
  if (!bloomreachConfig) return null;

  return (
    <Script id="bloomreach-sdk" strategy="afterInteractive">
      {/* eslint-disable-next-line max-len -- vendor snippet, kept verbatim/minified */}
      {String.raw`!function(e,t,n,i,o,r){const s=4e3,c="xnpe_async_hide";function a(e){if("number"!=typeof e)return e;const t=new Date;return new Date(t.getTime()+1e3*e)}function m(e){return e.reduce((function(e,t){return e[t]=function(){e._.push([t.toString(),arguments])},e}),{_:[]})}function p(e,t,n){const i=n.createElement(t);i.src=e;const o=n.getElementsByTagName(t)[0];return o.parentNode.insertBefore(i,o),i}function l(e){return"[object Date]"===Object.prototype.toString.call(e)}r.target=r.target||"https://api.exponea.com",r.file_path=r.file_path||r.target+"/js/brweb.min.js",o[t]=m(["anonymize","initialize","identify","getSegments","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers","_preInitialize","_initializeConfig","metadata"]),o[t].notifications=m(["isAvailable","isSubscribed","subscribe","unsubscribe"]),o[t].segments=m(["subscribe"]),o[t]["snippetVersion"]="v3.1.1",function(e,t,n,i){e[i]={sdk:e[n],sdkObjectName:n,skipExperiments:!!t.new_experiments,path:t.target,streamId:t.stream_id}}(o,r,t,i),function(e,t,n){p(e.file_path,t,n)}(r,n,e),function(e,t,n,i,o,r,m,u){if(!e.new_experiments)return;!0===e.new_experiments&&(e.new_experiments={});const f=e.new_experiments.hide_class||c,d=e.new_experiments.timeout||s,_=encodeURIComponent(r.location.href);let g;e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||l(e.cookies.expires)?g=a(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||l(e.cookies.expires.tracking))&&(g=a(e.cookies.expires.tracking))),g&&g<new Date&&(g=void 0);const k=e.target+"/webxp/streams/"+e.stream_id+"/"+t+"/"+(u.exec(m.cookie)||[0,"new"])[1]+"/modifications.min.js?http-referer="+_+"&timeout="+d+"ms"+(g?"&cookie-expires="+Math.floor(g.getTime()/1e3):"");"sync"===e.new_experiments.mode&&r.localStorage.getItem("__exponea__sync_modifications__")?function(e,t,n,i,o){n[o][t]="<"+t+' src="'+e+'"></'+t+">",i.writeln(n[o][t]),i.writeln("<"+t+">!"+o+".init && document.writeln("+o+"."+t+'.replace("/'+t+'/", "/'+t+'-async/").replace("><", " async><"))</'+t+">")}(k,t,r,m,n):function(e,t,n,i,o,r,s,c){r.documentElement.classList.add(e);const a=p(n,i,r);function m(){o[c].init||p(n.replace("/"+i+"/","/"+i+"-async/"),i,r)}function l(){r.documentElement.classList.remove(e)}a.onload=m,a.onerror=m,o.setTimeout(l,t),o[s]._revealPage=l}(f,d,k,t,r,m,o,n)}(r,n,i,0,t,o,e,RegExp("__exponea_etc__"+"=([\\w-]+)")),function(e,t,n){var i;e[t]._initializeConfig(n),(null===(i=n.experimental)||void 0===i?void 0:i.non_personalized_weblayers)&&e[t]._preInitialize(n),e[t].start=function(i){i&&Object.keys(i).forEach((e=>n[e]=i[e])),e[t].initialize(n)}}(o,t,r)}(document,"brweb","script","webxpClient",window,${bloomreachConfig});
brweb.start();
// not vendor code: the snippet is synchronous, so this signals "start() has run" to autoSubscribeToPushNotifications
window.__bloomreachStarted = true;
window.dispatchEvent(new Event('${BLOOMREACH_STARTED_EVENT}'));`}
    </Script>
  );
};

export default BloomreachInit;
